import { useEffect, useMemo, useState } from "react";
import {
  getJson, useJson, nf, ago, fmtDate, card, control, Chip, ConfidenceBadge, entityPath,
  walletPath, txPath, sanctionedPath, sanctionPath, CATEGORY_BY_SLUG, WALLET_TYPE, Box,
} from "./shared";
import { StixBar, StixTypeTag } from "./Stix";
import { graphHash, handOffBundle } from "../graph/handoff";
import { RelationshipsTab } from "./Profile";
import { RelationshipGraph } from "./Graph";
import { ReportedInTab } from "./Reports";
import { StixExport } from "./StixExport";

// Follow the money: cryptocurrency wallets tied to sanctions (OFAC), ransomware
// (Ransomwhere) and published reports, with their transactions from public block
// explorers. Lists: #/kb/wallets, #/kb/sanctioned, #/kb/ransom-payments. Pages:
// #/kb/wallet/<chain>/<address>, #/kb/tx/<chain>/<hash>, #/kb/sanctioned/<id>.

const PAGE = 50;
// Each chain's own brand colour, for its chip.
const CHAIN_COLOR = { btc: "#f7931a", eth: "#627eea", trx: "#e5484d", ltc: "#345d9d", xmr: "#f26822", bch: "#0ac18e", doge: "#c2a633", dash: "#008ce7", zec: "#c49a2c", sol: "#9945ff", xrp: "#6b7280", bsv: "#eab300", btg: "#eba809", xvg: "#00cbff", bnb: "#f0b90b" };
const CHAIN_SHORT = { btc: "BTC", eth: "ETH", trx: "TRON", ltc: "LTC", xmr: "XMR", bch: "BCH", doge: "DOGE", dash: "DASH", zec: "ZEC", sol: "SOL", xrp: "XRP", bsv: "BSV", btg: "BTG", xvg: "XVG", bnb: "BNB" };
const DIGITS = { BTC: 8, LTC: 8, ETH: 6, WETH: 6, WBTC: 8, TRX: 2, USDT: 2, USDC: 2, DAI: 2 };
const SANCTION_RED = "#dc2626";
const RANSOM_ORANGE = "#ea580c";
const CHAT_PURPLE = "#a855f7";
const groupHref = (e, tab) => (e ? `#/kb/e/${encodeURIComponent(e.attackId || e.id)}${tab ? `?tab=${tab}` : ""}` : null);

const short = (a, n = 10) => (a && a.length > n * 2 + 1 ? `${a.slice(0, n)}…${a.slice(-n + 2)}` : a || "");
export const usd = (n) => (n == null ? "—" : n >= 1e9 ? `$${(n / 1e9).toFixed(2)}B` : n >= 1e6 ? `$${(n / 1e6).toFixed(n >= 1e8 ? 0 : 1)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}K` : `$${Math.round(n)}`);
const amount = (v, asset) => `${Number(v).toLocaleString(undefined, { maximumFractionDigits: DIGITS[asset] ?? 6 })} ${asset || ""}`.trim();
// A transaction's value in dollars on the day: cents when small, whole dollars, then millions.
const usdThen = (n) => (n == null ? null : n >= 1e6 ? usd(n) : n >= 100 ? `$${Math.round(n).toLocaleString("en-US")}` : `$${n.toFixed(2)}`);
// A calendar date ("2022-09-04") shown as that day everywhere, whatever the time zone.
const fmtDay = (d) => (d ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00Z` : d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null);
const year = (iso) => (iso ? new Date(iso).getUTCFullYear() : null);
const years = (a, b) => (a && b ? (year(a) === year(b) ? `${year(a)}` : `${year(a)}–${year(b)}`) : a ? `${year(a)}` : "—");

export function ChainChip({ chain, ui, name }) {
  const c = CHAIN_COLOR[chain] || "#71717a";
  return <span title={name || CHAIN_SHORT[chain]} style={{ fontSize: ui.fs(10), fontWeight: 800, color: c, background: c + "18", border: `1px solid ${c}55`, borderRadius: 5, padding: "1px 6px", whiteSpace: "nowrap", letterSpacing: 0.4 }}>{CHAIN_SHORT[chain] || chain}</span>;
}

function Flag({ color, children, ui, title }) {
  return <span title={title} style={{ fontSize: ui.fs(10), fontWeight: 800, color, background: color + "16", border: `1px solid ${color}55`, borderRadius: 5, padding: "1px 6px", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: 0.6 }}>{children}</span>;
}

function Fact({ k, children, ui }) {
  return <span style={{ minWidth: 0, wordBreak: "break-word", fontSize: ui.fs(12) }}><span style={{ color: ui.B.textDim }}>{k}: </span><span style={{ color: ui.B.text }}>{children}</span></span>;
}

function CopyButton({ text, ui, label = "Copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button onClick={() => navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); })} style={{ ...ui.HP.navBtn, padding: "4px 10px", fontSize: ui.fs(11), whiteSpace: "nowrap" }}>{done ? "Copied ✓" : label}</button>
  );
}

const Mono = ({ children, ui, style }) => <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: ui.fs(12), wordBreak: "break-all", ...style }}>{children}</span>;

// What one Treasury press release says about a sanctioned listing and ransomware.
function TreasuryTie({ t, ui }) {
  const { B, fs } = ui;
  return (
    <div style={{ marginTop: 10, padding: "8px 12px", borderLeft: `3px solid ${SANCTION_RED}`, background: B.bg, borderRadius: 6, fontSize: fs(12), color: B.textMuted, lineHeight: 1.55 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 3 }}>
        <b style={{ color: B.text }}>
          {t.partial ? `${t.roleLabel}, partly attributed` : t.roleLabel}
          {t.groups.length > 0 && <> · {t.groups.map((g, i) => <span key={g.key}>{i ? ", " : ""}{g.entities[0] ? <a href={entityPath(g.entities[0])} style={{ color: B.text }}>{g.name}</a> : g.name}</span>)}</>}
        </b>
        <span style={{ color: B.textDim }}>US Treasury, {fmtDay(t.date)}</span>
        <ConfidenceBadge confidence={t.confidence} ui={ui} />
      </div>
      {t.what}
      {t.note && <div style={{ marginTop: 3, fontSize: fs(11), color: B.textDim }}>{t.note}</div>}
      <div style={{ marginTop: 4 }}><a href={t.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>Press release: “{t.title}” ↗</a></div>
    </div>
  );
}

// ─── Trace: paste an address or transaction hash ───

export function TraceBox({ ui, autoFocus }) {
  const { B, fs, HP } = ui;
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    const v = q.trim();
    if (!v) return;
    setBusy(true); setError("");
    try {
      const r = await getJson(`/api/kb/trace?q=${encodeURIComponent(v)}`);
      window.location.hash = r.kind === "wallet" ? walletPath(r.chain, r.address) : txPath(r.chains[0], r.hash);
    } catch (err) { setError(err.message); }
    setBusy(false);
  };
  return (
    <form onSubmit={go} style={{ ...card(ui), padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
      <label htmlFor="trace-input" style={{ fontSize: fs(13), fontWeight: 800, color: B.text }}>Trace a wallet or transaction</label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input id="trace-input" autoFocus={autoFocus} value={q} onChange={e => setQ(e.target.value)} placeholder="Bitcoin, Ethereum, Tron, Litecoin or Monero address, or a transaction hash" spellCheck={false} autoComplete="off"
          style={{ ...control(ui), flex: "1 1 320px", minWidth: 0, fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }} />
        <button type="submit" disabled={busy || !q.trim()} style={{ ...HP.primaryBtn, padding: "8px 16px", fontSize: fs(12) }}>{busy ? "Checking…" : "Trace"}</button>
      </div>
      {error ? <div style={{ fontSize: fs(12), color: "#dc2626" }}>{error}</div>
        : <div style={{ fontSize: fs(11), color: B.textDim }}>Any address works, not just the ones listed here: its checksum is checked, then Bitcoin, Ethereum, Tron and Litecoin transactions are looked up on free public block explorers.</div>}
    </form>
  );
}

function MoneyIntro({ slug, meta, ui, children }) {
  const { B, fs, getConf } = ui;
  const cat = CATEGORY_BY_SLUG[slug];
  const st = meta?.wallets;
  const loading = st && (!st.ofac?.builtAt || !st.ransomwhere?.builtAt);
  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "4px 0 6px", flexWrap: "wrap" }}>
        <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: 0, color: B.text }}>{cat.label}</h2>
        <span style={{ fontSize: fs(13), color: getConf(cat.type).color, fontWeight: 800 }}>{meta?.counts?.[slug] ? nf(meta.counts[slug]) : ""}</span>
        <StixTypeTag ui={ui} type={slug === "sanctioned" ? "identity" : slug === "wallets" ? WALLET_TYPE : "indicator"} />
      </div>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 6px", maxWidth: "110ch", lineHeight: 1.6 }}>{children || cat.blurb}</p>
      <div style={{ fontSize: fs(11), color: B.textDim, margin: "0 0 14px" }}>
        {st ? <>OFAC list {st.ofac?.builtAt ? `updated ${ago(st.ofac.builtAt)}` : st.ofac?.error ? `unavailable (${st.ofac.error})` : "loading…"} · Ransomwhere {st.ransomwhere?.builtAt ? `read ${ago(st.ransomwhere.builtAt)}` : st.ransomwhere?.error ? `unavailable (${st.ransomwhere.error})` : "loading…"}{loading ? " · the lists load a few minutes after the server starts" : ""}</> : "Loading…"}
      </div>
      <TraceBox ui={ui} />
    </>
  );
}

// ─── What's known about a wallet, in a row ───

function KnownFlags({ w, ui }) {
  const { B, fs } = ui;
  const flags = [];
  for (const s of w.sanctioned || []) flags.push(<a key={`s${s.ent}`} href={sanctionedPath(s.ent)} style={{ textDecoration: "none" }}><Flag color={SANCTION_RED} ui={ui} title={s.ransomware?.length ? `On OFAC's sanctions list; Treasury tied it to ransomware (${s.ransomware.join(", ")})` : "On OFAC's sanctions list"}>OFAC · {s.name}{s.ransomware?.length ? ` · ${s.ransomware.join(", ")}` : ""}</Flag></a>);
  for (const c of w.chats || []) flags.push(<a key={`c${c.group}`} href={groupHref(c.entity, "negotiations") || "#"} style={{ textDecoration: "none" }}><Flag color={CHAT_PURPLE} ui={ui} title="Given as the payment address in a leaked negotiation chat">Negotiation · {c.entity?.name || c.group}</Flag></a>);
  for (const n of w.notes || []) flags.push(<a key={`n${n.group}`} href={groupHref(n.entity, "notes") || "#"} style={{ textDecoration: "none" }}><Flag color={CHAT_PURPLE} ui={ui} title="The payment address printed in the group's ransom note">Ransom note · {n.entity?.name || n.group}</Flag></a>);
  if (w.family !== undefined) flags.push(<Flag key="r" color={RANSOM_ORANGE} ui={ui} title="Ransomware payment address (Ransomwhere)">Ransom{w.family ? ` · ${w.family}` : ""}</Flag>);
  if (w.reports) flags.push(<Chip key="rep" ui={ui}>{w.reports} report{w.reports === 1 ? "" : "s"}</Chip>);
  for (const t of w.tags || []) flags.push(<Chip key={t} ui={ui}>{t}</Chip>);
  return flags.length ? <span style={{ display: "inline-flex", gap: 5, flexWrap: "wrap" }}>{flags}</span> : <span style={{ fontSize: fs(11), color: B.textDim }}>—</span>;
}

export function WalletRows({ items, ui, showPayments = true }) {
  const { B, fs } = ui;
  const cell = { padding: "9px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };
  return (
    <div style={{ ...card(ui), overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
        <thead>
          <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
            <th style={{ padding: "9px 12px", fontWeight: 700 }}>Chain</th>
            <th style={{ padding: "9px 12px", fontWeight: 700 }}>Address</th>
            <th style={{ padding: "9px 12px", fontWeight: 700 }}>What's known</th>
            {showPayments && <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>Ransom received</th>}
          </tr>
        </thead>
        <tbody>
          {items.map(w => (
            <tr key={w.key}>
              <td style={cell}><ChainChip chain={w.chain} name={w.chainName} ui={ui} /></td>
              <td style={{ ...cell, maxWidth: 380 }}><a href={walletPath(w.chain, w.address)} style={{ color: B.text, textDecoration: "none", fontWeight: 600 }}><Mono ui={ui}>{w.address}</Mono></a></td>
              <td style={cell}><KnownFlags w={w} ui={ui} />{w.confidence && <span style={{ marginLeft: 6 }}><ConfidenceBadge confidence={w.confidence} ui={ui} /></span>}</td>
              {showPayments && <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap", color: w.usd || Number(w.received?.amount) ? B.text : B.textDim }}>{w.usd ? <>{usd(w.usd)}<div style={{ fontSize: fs(10), color: B.textDim }}>{nf(w.payments)} payment{w.payments === 1 ? "" : "s"}{w.lastPayment ? ` · last ${year(w.lastPayment)}` : ""}</div></> : Number(w.received?.amount) ? <>{amount(w.received.amount, w.received.unit)}<div style={{ fontSize: fs(10), color: B.textDim }}>received on-chain</div></> : "—"}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── #/kb/wallets ───

const SOURCES = [["", "All sources"], ["ofac", "Sanctioned (OFAC)"], ["chats", "From negotiation chats"], ["notes", "From ransom notes"], ["ransomware", "Ransomware payments"], ["reports", "Named in reports"]];
const SORTS = [["default", "Sanctioned first"], ["confidence", "Highest confidence"], ["usd", "Most ransom received"], ["payments", "Most payments"], ["recent", "Latest payment"]];

export function KbWalletList({ meta, ui, params = {}, asIndicators }) {
  const { B, fs } = ui;
  const [q, setQ] = useState(params.q || "");
  const [query, setQuery] = useState(params.q || "");
  const [source, setSource] = useState(params.source || "");
  const [chainKey, setChainKey] = useState(params.chain || "");
  const [sort, setSort] = useState(params.sort || (asIndicators ? "confidence" : "default"));
  const family = params.family || "";
  const ent = params.ent || "";
  const [pages, setPages] = useState([]);
  const [state, setState] = useState({ loading: true, error: "", total: 0, counts: null });

  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const base = useMemo(() => {
    const u = new URLSearchParams({ sort });
    if (query) u.set("q", query);
    if (source) u.set("source", source);
    if (chainKey) u.set("chain", chainKey);
    if (family) u.set("family", family);
    if (ent) u.set("ent", ent);
    return `/api/kb/wallets?${u}`;
  }, [query, source, chainKey, sort, family, ent]);
  useEffect(() => {
    let live = true;
    setState(s => ({ ...s, loading: true, error: "" }));
    getJson(`${base}&offset=0&limit=${PAGE}`, { fresh: true })
      .then(d => { if (!live) return; setPages([d.items]); setState({ loading: false, error: "", total: d.total, counts: d.counts }); })
      .catch(e => live && setState(s => ({ ...s, loading: false, error: e.message })));
    return () => { live = false; };
  }, [base]);
  const items = pages.flat();
  const more = () => getJson(`${base}&offset=${items.length}&limit=${PAGE}`).then(d => setPages(p => [...p, d.items])).catch(() => {});
  const chains = Object.entries(state.counts?.chains || {}).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <MoneyIntro slug={asIndicators ? "crypto-wallets" : "wallets"} meta={meta} ui={ui}>
        {asIndicators && <>Cryptocurrency wallets are indicators like IP addresses and file hashes: each has a confidence score and its own page with its relationships, graph, STIX export and transactions. </>}
        Cryptocurrency addresses from five free sources: OFAC's sanctions list (the US Treasury publishes the addresses of people and organisations it sanctions, and its press releases say which ransomware groups some of them belong to), the payment addresses ransomware groups gave victims in leaked negotiation chats or printed in their ransom notes, Ransomwhere's crowdsourced ransomware payment records, and addresses quoted in the published reports. Open one to see what's known about it and its transactions, valued in dollars on the day, then follow the money wallet by wallet.
      </MoneyIntro>
      {(family || ent) && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, fontSize: fs(12), color: B.textMuted }}>
          Showing {family ? <>the <b style={{ color: B.text }}>{family === "(unattributed)" ? "unattributed" : family}</b> ransomware payment addresses</> : <>the addresses OFAC lists for one entity</>}.
          <a href="#/kb/wallets" style={{ color: B.accent }}>Show all wallets</a>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by address, owner, programme or ransomware family…" aria-label="Filter wallets" style={{ ...control(ui), flex: "1 1 260px", minWidth: 0 }} />
        <select value={source} onChange={e => setSource(e.target.value)} aria-label="Source" style={control(ui)}>
          {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}{v && state.counts ? ` (${nf(state.counts.sources[v])})` : ""}</option>)}
        </select>
        <select value={chainKey} onChange={e => setChainKey(e.target.value)} aria-label="Blockchain" style={control(ui)}>
          <option value="">All blockchains</option>
          {chains.map(([c, n]) => <option key={c} value={c}>{CHAIN_SHORT[c] || c} ({nf(n)})</option>)}
          {chainKey && !chains.some(([c]) => c === chainKey) && <option value={chainKey}>{CHAIN_SHORT[chainKey] || chainKey}</option>}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort" style={control(ui)}>
          {SORTS.map(([v, l]) => <option key={v} value={v}>Sort: {l}</option>)}
        </select>
      </div>
      <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 10 }}>{state.loading ? "Loading…" : `${nf(state.total)} wallet${state.total === 1 ? "" : "s"}`}</div>
      {state.error && <div style={{ color: "#dc2626", fontSize: fs(12), marginBottom: 10 }}>{state.error}</div>}
      {!!items.length && <WalletRows items={items} ui={ui} />}
      {items.length < state.total && <button onClick={more} style={{ ...control(ui), cursor: "pointer", marginTop: 14 }}>Show {Math.min(PAGE, state.total - items.length)} more</button>}
    </>
  );
}

// ─── #/kb/sanctioned ───

export function KbSanctionedList({ meta, ui, params = {} }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const tie = params.tie === "ransomware";
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [program, setProgram] = useState("");
  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const { data, error, loading } = useJson(tie ? null : `/api/kb/wallets/sanctioned?${new URLSearchParams({ q: query, program })}`);
  const programs = Object.entries(data?.programs || {}).sort((a, b) => b[1] - a[1]);
  const viewBtn = (on) => ({ ...ui.HP.navBtn, padding: "6px 12px", fontSize: fs(12), textDecoration: "none", ...(on ? { color: B.text, borderColor: B.primary, background: B.primary + "14", fontWeight: 700 } : {}) });
  return (
    <>
      <MoneyIntro slug="sanctioned" meta={meta} ui={ui}>
        Everyone on the US Treasury's Specially Designated Nationals list (OFAC) who is listed with cryptocurrency addresses: darknet markets, exchanges and mixers that laundered for criminals, ransomware and hacking crews, North Korean and Iranian networks, drug traffickers and terrorist financiers. US persons may not deal with them; for everyone else the listing is the US government's own attribution of these addresses. All of OFAC's listings, and the UN's, the EU's and the UK's, are under <a href="#/kb/sanctions" style={{ color: B.accent }}>Sanctions</a>.
      </MoneyIntro>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <a href="#/kb/sanctioned" style={viewBtn(!tie)}>All listings with crypto addresses</a>
        <a href="#/kb/sanctioned?tie=ransomware" style={viewBtn(tie)}>Sanctioned for ransomware</a>
      </div>
      {tie ? <RansomwareDesignations ui={ui} /> : <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by name, alias or programme…" aria-label="Filter sanctioned entities" style={{ ...control(ui), flex: "1 1 260px", minWidth: 0 }} />
        <select value={program} onChange={e => setProgram(e.target.value)} aria-label="Sanctions programme" style={control(ui)}>
          <option value="">All programmes</option>
          {programs.map(([p, n]) => <option key={p} value={p}>{p} ({n})</option>)}
        </select>
      </div>
      <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 10 }}>{loading ? "Loading…" : `${nf(data?.total || 0)} listed`}</div>
      {error && <div style={{ color: "#dc2626", fontSize: fs(12), marginBottom: 10 }}>{error}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))", gap: 12 }}>
        {(data?.items || []).map(e => (
          <a key={e.ent} href={sanctionedPath(e.ent)} style={{ display: "flex", flexDirection: "column", gap: 8, textDecoration: "none", ...card(ui), padding: 16, minWidth: 0, boxShadow: `inset 3px 0 0 ${SANCTION_RED}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <TypeIcon type="identity" color={getConf("identity").color} size={15} />
              <span style={{ flex: 1, fontSize: fs(14), fontWeight: 800, color: B.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.name}>{e.name}</span>
              <span style={{ fontSize: fs(11), color: B.textDim, whiteSpace: "nowrap" }}>{e.kind === "individual" ? "Person" : "Organisation"}</span>
            </div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{e.programs.map(p => <Chip key={p.code} ui={ui} title={p.code}>{p.label}</Chip>)}</div>
            {e.linkedTo.length > 0 && <div style={{ fontSize: fs(11), color: B.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Linked to {e.linkedTo.join("; ")}</div>}
            {e.ransomware?.length > 0 && <div style={{ fontSize: fs(11), color: SANCTION_RED, fontWeight: 700 }}>Ransomware: {e.ransomware.join(", ")}</div>}
            <div style={{ marginTop: "auto", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", fontSize: fs(11), color: B.textDim }}>
              <b style={{ color: B.text }}>{nf(e.wallets)}</b> address{e.wallets === 1 ? "" : "es"} {e.chains.map(c => <ChainChip key={c} chain={c} ui={ui} />)}
              {e.group && <span style={{ marginLeft: "auto" }}>ATT&CK: {e.group.name}</span>}
            </div>
          </a>
        ))}
      </div>
      </>}
    </>
  );
}

// Treasury's ransomware designations, newest first: who was named, in what role, for which group.
function RansomwareDesignations({ ui }) {
  const { B, fs } = ui;
  const { data, error, loading } = useJson("/api/kb/wallets/sanctioned/ransomware");
  if (loading) return <div style={{ fontSize: fs(12), color: B.textDim }}>Loading…</div>;
  if (error) return <div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div>;
  return (
    <>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 14px", maxWidth: "110ch", lineHeight: 1.6 }}>
        OFAC's list says who is sanctioned but not which ransomware group they belong to or served; Treasury's press release does. These are the {data.actions.length} ransomware-related actions since 2021, written up by hand from each press release ({nf(data.listings)} people and businesses, {nf(data.withWallets)} of them with {nf(data.wallets)} crypto addresses on their listing). Members of a group (leaders, developers, affiliates) have their addresses linked to the group as indicators; exchanges, launderers and hosting companies are linked to the groups they served, without their addresses.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {data.actions.map(a => (
          <div key={a.pr} style={{ ...card(ui), padding: "14px 18px", boxShadow: `inset 3px 0 0 ${SANCTION_RED}` }}>
            <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", marginBottom: 6 }}>
              <span style={{ fontSize: fs(12), color: B.textDim, whiteSpace: "nowrap" }}>{fmtDay(a.date)}</span>
              <a href={a.url} target="_blank" rel="noreferrer noopener" style={{ flex: "1 1 320px", fontSize: fs(14), fontWeight: 800, color: B.text, textDecoration: "none" }}>{a.title} ↗</a>
            </div>
            {a.groups.length > 0 && (
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 6 }}>
                {a.groups.map(g => (g.entities[0]
                  ? <a key={g.key} href={entityPath(g.entities[0])} style={{ textDecoration: "none" }}><Chip ui={ui} color={RANSOM_ORANGE}>{g.name}</Chip></a>
                  : <Chip key={g.key} ui={ui}>{g.name}</Chip>))}
              </div>
            )}
            {a.note && <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 6, lineHeight: 1.55 }}>{a.note}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {a.designees.map(d => (
                <div key={d.ent} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", fontSize: fs(12), borderTop: `1px solid ${B.border}`, paddingTop: 6 }}>
                  <a href={sanctionedPath(d.ent)} style={{ color: B.text, fontWeight: 700, textDecoration: "none", minWidth: 0 }}>{d.name}</a>
                  <Chip ui={ui} color={d.part ? SANCTION_RED : undefined}>{d.roleLabel}</Chip>
                  <span style={{ fontSize: fs(11), color: d.wallets ? B.text : B.textDim }}>{d.wallets ? `${nf(d.wallets)} crypto address${d.wallets === 1 ? "" : "es"}` : "no crypto addresses listed"}</span>
                  <span style={{ flexBasis: "100%", color: B.textMuted, lineHeight: 1.5 }}>{d.what}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── #/kb/ransom-payments ───

export function KbRansomFamilies({ meta, ui }) {
  const { B, fs } = ui;
  const { data, error, loading } = useJson("/api/kb/wallets/families");
  const cell = { padding: "9px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12) };
  const num = { ...cell, textAlign: "right", whiteSpace: "nowrap" };
  return (
    <>
      <MoneyIntro slug="ransom-payments" meta={meta} ui={ui}>
        What ransomware victims paid, as far as public records show: Ransomwhere collected payment addresses from victims, researchers and reports, and read the payments from the Bitcoin blockchain, valued in dollars on the day. It covers a small part of all ransom paid (most addresses are never made public) and stops in 2024. Families are as Ransomwhere names them; "Unattributed" addresses were reported as ransomware without a family. See also <a href="#/kb/sanctioned?tie=ransomware" style={{ color: B.accent }}>who the US Treasury sanctioned for ransomware</a>.
      </MoneyIntro>
      {error && <div style={{ color: "#dc2626", fontSize: fs(12), marginBottom: 10 }}>{error}</div>}
      {data && (
        <div style={{ display: "flex", gap: "6px 22px", flexWrap: "wrap", marginBottom: 12 }}>
          <Fact k="Total recorded" ui={ui}><b>{usd(data.totalUsd)}</b></Fact>
          <Fact k="Payments" ui={ui}>{nf(data.totalPayments)}</Fact>
          <Fact k="Families" ui={ui}>{nf(data.items.filter(f => f.name).length)}</Fact>
        </div>
      )}
      {loading && <div style={{ fontSize: fs(12), color: B.textDim }}>Loading…</div>}
      {data && (
        <div style={{ ...card(ui), overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
            <thead>
              <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                <th style={{ padding: "9px 12px", fontWeight: 700 }}>Family</th>
                <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>Received</th>
                <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>Payments</th>
                <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>Wallets</th>
                <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>Years</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(f => (
                <tr key={f.key}>
                  <td style={cell}>
                    <a href={`#/kb/wallets?family=${encodeURIComponent(f.key)}`} style={{ color: B.text, fontWeight: 700, textDecoration: "none" }}>{f.name || "Unattributed"}</a>
                    {f.entity && <a href={entityPath(f.entity)} style={{ marginLeft: 8, fontSize: fs(11), color: B.accent, textDecoration: "none" }}>{f.entity.name} profile →</a>}
                  </td>
                  <td style={{ ...num, fontWeight: 700, color: B.text }}>{usd(f.usd)}</td>
                  <td style={{ ...num, color: B.textMuted }}>{nf(f.payments)}</td>
                  <td style={{ ...num, color: B.textMuted }}>{nf(f.wallets)}</td>
                  <td style={{ ...num, color: B.textDim }}>{years(f.first, f.last)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── A transaction's parties ───

function Party({ p, chain, self, ui }) {
  const { B, fs } = ui;
  if (!p.address) return <span style={{ fontSize: fs(11), color: B.textDim }}>(no address)</span>;
  const isSelf = self && p.address.toLowerCase() === self.toLowerCase();
  const special = p.address === "coinbase" || p.address === "OP_RETURN";
  const tags = [...(p.tags || []), ...(p.explorerTags || [])];
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "baseline", flexWrap: "wrap", minWidth: 0 }}>
      {isSelf ? <b style={{ fontSize: fs(11), color: B.text }}>this wallet</b>
        : special ? <span style={{ fontSize: fs(11), color: B.textDim }}>{p.address === "coinbase" ? "newly mined" : "data (OP_RETURN)"}</span>
        : <a href={walletPath(chain, p.address)} title={p.address} style={{ color: B.text, textDecoration: "none" }}><Mono ui={ui} style={{ fontSize: fs(11) }}>{short(p.address, 8)}</Mono></a>}
      {tags.slice(0, 3).map(t => (
        <span key={t} style={{ fontSize: fs(10), fontWeight: 700, color: /^OFAC|SANCTION/i.test(t) ? SANCTION_RED : /^Ransom/i.test(t) ? RANSOM_ORANGE : B.textMuted, background: B.bg, border: `1px solid ${B.border}`, borderRadius: 4, padding: "0 5px" }}>{t}</span>
      ))}
    </span>
  );
}

function TxTable({ txs, chain, self, ui }) {
  const { B, fs } = ui;
  const [showDust, setShowDust] = useState(false);
  const dust = txs.filter(t => t.dust).length;
  const shown = showDust ? txs : txs.filter(t => !t.dust);
  const cell = { padding: "9px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };
  const others = (list) => list.filter(p => !p.address || p.address.toLowerCase() !== self.toLowerCase());
  return (
    <>
      {dust > 0 && (
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: fs(12), color: B.textMuted, marginBottom: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={showDust} onChange={e => setShowDust(e.target.checked)} />
          Show {dust} dust transfer{dust === 1 ? "" : "s"} (tiny amounts spammers and scammers send to wallets; hidden by default)
        </label>
      )}
      {!shown.length && <div style={{ fontSize: fs(12), color: B.textDim }}>{txs.length ? "Only dust in the latest transactions." : "No transactions."}</div>}
      {shown.length > 0 && (
        <div style={{ ...card(ui), overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                <th style={{ padding: "9px 12px", fontWeight: 700 }}>When</th>
                <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>Amount</th>
                <th style={{ padding: "9px 12px", fontWeight: 700 }}>From / to</th>
                <th style={{ padding: "9px 12px", fontWeight: 700 }}>Transaction</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(t => {
                const out = t.direction === "out";
                const parties = out ? others(t.outputs) : others(t.inputs);
                const hidden = (out ? t.moreOut : t.moreIn) || 0;
                return (
                  <tr key={t.hash + (t.net || []).map(n => n.asset).join("")} style={{ opacity: t.dust ? 0.6 : 1 }}>
                    <td style={{ ...cell, whiteSpace: "nowrap", color: B.textMuted }}>{t.time ? fmtDate(t.time) : "unconfirmed"}{t.status === "failed" && <div style={{ fontSize: fs(10), color: "#dc2626" }}>failed</div>}</td>
                    <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                      {(t.net || []).map(n => {
                        const neg = String(n.amount).startsWith("-");
                        return (
                          <div key={n.asset}>
                            <div style={{ fontWeight: 700, color: neg ? RANSOM_ORANGE : "#16a34a" }}>{neg ? "−" : "+"}{amount(String(n.amount).replace(/^-/, ""), n.asset)}</div>
                            {n.usd >= 0.01 && <div style={{ fontSize: fs(10.5), color: B.textMuted }} title={`Worth about ${usdThen(n.usd)} on ${fmtDate(t.time)}`}>≈ {usdThen(n.usd)} then</div>}
                          </div>
                        );
                      })}
                      <div style={{ fontSize: fs(10), color: B.textDim }}>{t.direction === "self" ? "to itself" : out ? "sent" : "received"}</div>
                    </td>
                    <td style={cell}>
                      <div style={{ fontSize: fs(10), color: B.textDim, marginBottom: 3 }}>{out ? "to" : "from"}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {parties.slice(0, 4).map((p, i) => <Party key={`${p.address}${i}`} p={p} chain={chain} self={self} ui={ui} />)}
                        {(parties.length > 4 || hidden > 0) && <span style={{ fontSize: fs(11), color: B.textDim }}>and {parties.length - Math.min(4, parties.length) + hidden} more</span>}
                        {!parties.length && <span style={{ fontSize: fs(11), color: B.textDim }}>only this wallet</span>}
                      </div>
                    </td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>
                      <a href={txPath(chain, t.hash)} style={{ color: B.accent, textDecoration: "none" }}><Mono ui={ui} style={{ fontSize: fs(11) }}>{short(t.hash, 6)}</Mono></a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── #/kb/wallet/<chain>/<address> ───

// The explorer lookup for a wallet, with WalletExplorer's names for its biggest
// Bitcoin counterparties (exchanges, markets…) once they arrive.
function useOnChain(data) {
  const { data: c, error, loading } = useJson(data?.canLookUp ? `/api/kb/wallet/${data.chain}/${encodeURIComponent(data.address)}/chain` : null);
  const [names, setNames] = useState({});
  useEffect(() => {
    if (!c || data.chain !== "btc") return undefined;
    const amounts = new Map();
    for (const t of c.txs) for (const p of [...t.inputs, ...t.outputs]) if (p.address && p.address !== data.address) amounts.set(p.address, (amounts.get(p.address) || 0) + Number(p.amount || 0));
    const top = [...amounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 15).map(([a]) => a);
    if (!top.length) return undefined;
    let live = true;
    getJson(`/api/kb/labels/btc?a=${top.join(",")}`).then(r => { if (live) setNames(r.labels || {}); }).catch(() => {});
    return () => { live = false; };
  }, [c, data?.chain, data?.address]);
  const txs = useMemo(() => (c ? c.txs.map(t => {
    const name = (p) => (p.address && names[p.address]?.label ? { ...p, explorerTags: [...(p.explorerTags || []), `WalletExplorer: ${names[p.address].label}`] } : p);
    return { ...t, inputs: t.inputs.map(name), outputs: t.outputs.map(name) };
  }) : []), [c, names]);
  return { c, error, loading, txs };
}

function OnChainSummary({ data, chainState, ui, onTab }) {
  const { B, fs, HP } = ui;
  const { c, error, loading } = chainState;
  const btn = { ...HP.navBtn, padding: "6px 12px", fontSize: fs(12), textDecoration: "none", display: "inline-flex", alignItems: "center" };
  if (!data.canLookUp) {
    return (
      <Box title="On the blockchain" ui={ui}>
        <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.6 }}>
          {data.explorer?.note || `${data.chainName} transactions can't be looked up here yet.`}
          {data.explorer?.url && <> <a href={data.explorer.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>Open it on {data.explorer.name} ↗</a></>}
        </div>
      </Box>
    );
  }
  if (loading || (!c && !error)) return <Box title="On the blockchain" ui={ui}><div style={{ fontSize: fs(12), color: B.textDim }}>Looking it up on {data.explorer?.name}…</div></Box>;
  if (error) return <Box title="On the blockchain" ui={ui}><div style={{ fontSize: fs(12), color: "#dc2626" }}>{error}</div>{data.explorer?.url && <a href={data.explorer.url} target="_blank" rel="noreferrer noopener" style={{ ...btn, marginTop: 10 }}>Open on {data.explorer.name} ↗</a>}</Box>;
  const unit = c.unit;
  return (
    <Box title="On the blockchain" ui={ui} right={<span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>from {c.provider.name}, {ago(c.fetchedAt)}</span>}>
      <div style={{ display: "flex", gap: "8px 24px", flexWrap: "wrap", alignItems: "baseline" }}>
        <Fact k="Balance" ui={ui}><b>{amount(c.balance, unit)}</b>{c.usdRate && Number(c.balance) > 0 ? <span style={{ color: B.textDim }}> (≈{usd(Number(c.balance) * c.usdRate)})</span> : null}</Fact>
        {(c.tokenBalances || []).map(t => <Fact key={t.asset} k={t.asset} ui={ui}><b>{amount(t.amount, t.asset)}</b></Fact>)}
        {c.received != null && <Fact k="Received in total" ui={ui}>{amount(c.received, unit)}</Fact>}
        {c.sent != null && <Fact k="Sent in total" ui={ui}>{amount(c.sent, unit)}</Fact>}
        {c.txCount != null && <Fact k="Transactions" ui={ui}>{nf(c.txCount)}</Fact>}
        {c.tokenTransfers != null && <Fact k="Token transfers" ui={ui}>{nf(c.tokenTransfers)}</Fact>}
        {c.firstSeen && <Fact k="Created" ui={ui}>{fmtDate(c.firstSeen)}</Fact>}
        {c.lastSeen && <Fact k="Last active" ui={ui}>{fmtDate(c.lastSeen)}</Fact>}
        {c.isContract && <Fact k="Type" ui={ui}>Smart contract</Fact>}
      </div>
      {c.labels?.length > 0 && (
        <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", fontSize: fs(12), color: B.textDim }}>
          {c.provider.name} labels: {c.labels.map(l => <Chip key={l} ui={ui} color={/sanction|ofac|exploit|hack|phish|scam/i.test(l) ? SANCTION_RED : undefined}>{l}</Chip>)}
        </div>
      )}
      {c.oracle && !c.oracle.error && (
        <div style={{ marginTop: 10, fontSize: fs(12), color: B.textMuted, lineHeight: 1.55 }}>
          <b style={{ color: c.oracle.sanctioned ? SANCTION_RED : B.text }}>Chainalysis Sanctions Oracle: {c.oracle.sanctioned ? "listed as sanctioned" : "not listed"}.</b>{" "}
          Read from Chainalysis's free on-chain sanctions contract on Ethereum. Chainalysis stopped updating it on {new Date(`${c.oracle.frozenSince}T12:00:00Z`).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" })}, so later designations are missing; OFAC's own list (above) is current.
        </div>
      )}
      {c.walletExplorer && (
        <div style={{ marginTop: 10, fontSize: fs(12), color: B.textMuted, lineHeight: 1.55 }}>
          <b style={{ color: B.text }}>WalletExplorer:</b> {c.walletExplorer.label ? <>this address belongs to <b style={{ color: B.text }}>{c.walletExplorer.label}</b></> : "part of an unnamed wallet (addresses that are spent together, so most likely one owner)"}.{" "}
          {c.walletExplorer.url && <a href={c.walletExplorer.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>See the whole wallet ↗</a>}
        </div>
      )}
      {c.note && <div style={{ marginTop: 10, fontSize: fs(12), color: B.textDim }}>{c.note}</div>}
      {data.trail && (
        <div style={{ marginTop: 10, fontSize: fs(12), color: B.textMuted }}>
          Someone followed its money {ago(data.trail.at)}: {data.trail.hops} hop{data.trail.hops === 1 ? "" : "s"}{data.trail.service ? <>, to <b style={{ color: B.text }}>{data.trail.service}</b></> : ""}.{" "}
          <button onClick={() => onTab("trail")} style={{ background: "none", border: "none", padding: 0, color: B.accent, cursor: "pointer", fontSize: fs(12) }}>See the trail</button>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        {c.txs.length > 0 && <button onClick={() => onTab("transactions")} style={{ ...HP.primaryBtn, padding: "7px 14px", fontSize: fs(12) }}>See the transactions</button>}
        {TRACE_CHAINS.has(data.chain) && <button onClick={() => onTab("trail")} style={btn}>Where did the money go?</button>}
        <a href={graphHash(data.key)} style={btn} title="Open this wallet and its counterparties as a graph; double-click any wallet there to load its transactions">Follow the money in the Graph →</a>
        <a href={c.provider.url} target="_blank" rel="noreferrer noopener" style={btn}>Full history on {c.provider.site || c.provider.name} ↗</a>
      </div>
    </Box>
  );
}

function TxBox({ data, chainState, ui }) {
  const { B, fs, HP } = ui;
  const { c, error, loading, txs } = chainState;
  // Older pages, loaded on request (each is a lookup on the explorer).
  const [older, setOlder] = useState({ pages: [], next: undefined, scannedTo: null, loading: false, error: "" });
  useEffect(() => { setOlder({ pages: [], next: undefined, scannedTo: null, loading: false, error: "" }); }, [c]);
  if (!data.canLookUp) return <OnChainSummary data={data} chainState={chainState} ui={ui} onTab={() => {}} />;
  if (loading || (!c && !error)) return <Box ui={ui}><div style={{ fontSize: fs(12), color: B.textDim }}>Looking it up on {data.explorer?.name}…</div></Box>;
  if (error) return <Box ui={ui}><div style={{ fontSize: fs(12), color: "#dc2626" }}>{error}</div></Box>;
  const next = older.next === undefined ? c.next : older.next;
  const loadOlder = async () => {
    setOlder(o => ({ ...o, loading: true, error: "" }));
    try {
      const p = await getJson(`/api/kb/wallet/${data.chain}/${encodeURIComponent(data.address)}/chain?before=${encodeURIComponent(next)}`);
      setOlder(o => ({ pages: [...o.pages, p.txs], next: p.next || null, scannedTo: p.scannedTo || null, loading: false, error: "" }));
    } catch (e) { setOlder(o => ({ ...o, loading: false, error: e.message })); }
  };
  const seen = new Set();
  const all = [...txs, ...older.pages.flat()].filter(t => { const k = t.hash + (t.net || []).map(n => n.asset).join(""); if (seen.has(k)) return false; seen.add(k); return true; });
  // The last page read found no transfers at all (Ethereum and Tron): say how far back it looked.
  const lastPage = older.pages.length ? older.pages[older.pages.length - 1] : c.txs;
  const scannedTo = older.pages.length ? older.scannedTo : c.scannedTo;
  const emptyStretch = !!next && !lastPage.length && !!scannedTo;
  const kinds = data.chain === "trx" ? ["TRX, USDT or USDC", "votes, staking, resource delegation or contract calls"] : [`${c.unit || "ETH"} or major-token`, "contract calls or other tokens"];
  return (
    <Box title={`Transactions, newest first${c.txCount ? ` (${nf(all.length)} of ${nf(c.txCount)})` : all.length ? ` (${nf(all.length)})` : ""}`} ui={ui} right={<a href={c.provider.url} target="_blank" rel="noreferrer noopener" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500, color: B.accent }}>Full history on {c.provider.site || c.provider.name} ↗</a>}>
      {c.knownCounterparties > 0 && <div style={{ marginBottom: 10, fontSize: fs(12), color: B.textMuted }}>{c.knownCounterparties} counterpart{c.knownCounterparties === 1 ? "y is" : "ies are"} known to our sources{data.chain === "eth" ? ` or labelled by ${c.provider.name}` : ""}; they're tagged.</div>}
      {c.note && !c.txs.length && <div style={{ fontSize: fs(12), color: B.textDim }}>{c.note}</div>}
      {(all.length > 0 || !next) && <TxTable txs={all} chain={data.chain} self={data.address} ui={ui} />}
      {emptyStretch && (
        <div style={{ marginTop: all.length ? 10 : 0, fontSize: fs(12), color: B.textMuted, lineHeight: 1.55 }}>
          No {kinds[0]} transfers {all.length ? "in the next stretch, " : ""}back to {fmtDate(scannedTo)}: that part of its history is all other activity ({kinds[1]}). Busy wallets can have a lot of it; keep looking further back, or see the full history on {c.provider.site || c.provider.name}.
        </div>
      )}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        {next && <button onClick={loadOlder} disabled={older.loading} style={{ ...HP.navBtn, padding: "6px 12px", fontSize: fs(12) }}>{older.loading ? "Loading older transactions…" : emptyStretch ? "Keep looking further back" : "Show older transactions"}</button>}
        {!next && older.pages.length > 0 && <span style={{ fontSize: fs(12), color: B.textDim }}>That's the whole history.</span>}
        {older.error && <span style={{ fontSize: fs(12), color: "#dc2626" }}>{older.error}</span>}
        {all.some(t => t.net?.some(n => n.usd != null)) && <span style={{ fontSize: fs(11), color: B.textDim, marginLeft: "auto" }}>Dollar values use the day's closing price (stablecoins at face value).</span>}
      </div>
    </Box>
  );
}

// ─── Where did the money go? ───

const TRACE_CHAINS = new Set(["btc", "ltc", "eth", "trx"]);
const STOP_TEXT = {
  service: (s) => `Reached ${s.name} (named by ${s.source}). Money arriving at an exchange or service is usually where it gets cashed out or mixed; the service may know who deposited it.`,
  sanctioned: (s) => `Reached a wallet on OFAC's sanctions list: ${s.name.replace(/^OFAC: /, "")}. Keep going to see where it went from there.`,
  held: (s) => `The money stops here: this wallet still holds ${Number(s.balance) > 0 ? `${Number(s.balance).toLocaleString(undefined, { maximumFractionDigits: 8 })} ${s.unit}` : ""}${(s.tokens || []).filter(t => Number(t.amount) > 0).map(t => ` ${Number(t.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${t.asset}`).join(",")}.`,
  unspent: () => "The money stops here: this wallet hasn't paid anything on.",
  busy: (s) => `Reached a very busy address${s.txCount ? ` (${Number(s.txCount).toLocaleString()} transactions)` : ""}: almost always an exchange, payment service or mixer, even when no one has named it. This is usually where the public trail ends; the service itself may know who deposited the money.`,
  older: () => "This wallet has a long history, and the payment that moved the money on is further back than we read during a trace. Open the wallet and use Show older transactions to look for it.",
  "no-destination": () => "The next payment didn't go to an address we can follow.",
  loop: () => "The money came back to a wallet already on the path (often the owner moving it between their own addresses).",
  "max-hops": (s) => `Stopped after ${s.hops} more hops without reaching a named service. Keep going to follow it further.`,
  limit: (s) => s.message,
  error: (s) => `A lookup failed (${s.message}). Try again in a minute.`,
};

function FlowBox({ data, ui }) {
  const { B, fs, HP } = ui;
  const [hops, setHops] = useState(5);
  const [state, setState] = useState({ loading: false, error: "", result: null });
  // The trail kept on the server for this wallet (anyone's latest trace): no new lookups.
  useEffect(() => {
    if (!data.trail) return undefined;
    let live = true;
    getJson(`/api/kb/trail/${data.chain}/${encodeURIComponent(data.address)}`, { fresh: true })
      .then(t => { if (live) setState(s => (s.result || s.loading ? s : { loading: false, error: "", result: t })); })
      .catch(() => {});
    return () => { live = false; };
  }, [data.chain, data.address, data.trail]);
  const run = async () => {
    setState({ loading: true, error: "", result: null });
    try {
      const t = await getJson(`/api/kb/flow/${data.chain}/${encodeURIComponent(data.address)}?hops=${hops}`, { fresh: true });
      setState({ loading: false, error: "", result: { ...t, at: t.keptAt || null } });
    }
    catch (e) { setState({ loading: false, error: e.message, result: null }); }
  };
  // Carry on from the last wallet: its first payment out after the money arrived there.
  // root tells the server which kept trail to add the new hops to.
  const more = async () => {
    const prev = state.result;
    const last = prev.steps[prev.steps.length - 1];
    setState(st => ({ ...st, loading: true, error: "" }));
    try {
      const next = await getJson(`/api/kb/flow/${data.chain}/${encodeURIComponent(last.to)}?hops=${hops}&root=${encodeURIComponent(prev.start)}${last.tx.time ? `&after=${encodeURIComponent(last.tx.time)}` : ""}`, { fresh: true });
      const objects = new Map([...(prev.bundle?.objects || []), ...(next.bundle?.objects || [])].map(o => [o.id, o]));
      setState({ loading: false, error: "", result: { ...prev, steps: [...prev.steps, ...next.steps], stop: next.stop, at: next.keptAt || prev.at, bundle: prev.bundle || next.bundle ? { ...(prev.bundle || next.bundle), objects: [...objects.values()] } : null } });
    } catch (e) { setState(st => ({ ...st, loading: false, error: e.message })); }
  };
  const r = state.result;
  const canContinue = r && r.steps.length > 0 && ["max-hops", "sanctioned", "loop"].includes(r.stop.reason);
  return (
    <Box title="Where did the money go?" ui={ui}>
      <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.6, marginBottom: 10 }}>
        Follows the money hop by hop: at each wallet, the first payment it made after the money arrived, and that payment's biggest recipient, until it reaches a named exchange or service{data.chain === "btc" ? " (names from WalletExplorer)" : data.chain === "eth" ? " (names from Blockscout)" : ""}, a sanctioned wallet, or a wallet still holding it. It's a lead, not proof: mixers, change addresses and batched payments all blur the trail. Each trace is kept on the server, so the next person to open this wallet sees it without new lookups, and the wallet's STIX bundle and graph include it.
      </div>
      {r?.at && !state.loading && <div style={{ fontSize: fs(11.5), color: B.textDim, marginBottom: 8 }}>Traced {ago(r.at)}{r.steps.length ? `, ${r.steps.length} hop${r.steps.length === 1 ? "" : "s"}` : ""}. Trace again to follow it with today's data.</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={run} disabled={state.loading} style={{ ...HP.primaryBtn, padding: "7px 14px", fontSize: fs(12) }}>{state.loading ? "Following the money…" : r ? "Trace again" : "Trace it"}</button>
        <label style={{ fontSize: fs(12), color: B.textDim, display: "inline-flex", gap: 6, alignItems: "center" }}>up to
          <select value={hops} onChange={e => setHops(Number(e.target.value))} style={{ ...control(ui), padding: "4px 6px" }}>{[3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}</select> hops
        </label>
        {state.loading && <span style={{ fontSize: fs(11), color: B.textDim }}>Each hop is a lookup on a free explorer, a second or two each.</span>}
      </div>
      {state.error && <div style={{ marginTop: 10, fontSize: fs(12), color: "#dc2626" }}>{state.error}</div>}
      {r && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <FlowWallet address={r.start} chain={r.chain} ui={ui} first />
            {r.steps.map((s, i) => (
              <div key={i}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0 6px 14px", borderLeft: `2px solid ${B.primary}66`, marginLeft: 10, fontSize: fs(12), color: B.textMuted, flexWrap: "wrap" }}>
                  <span style={{ color: B.primary, fontWeight: 800 }}>↓</span>
                  <b style={{ color: B.text }}>{amount(s.tx.amount, s.tx.asset)}</b>
                  {s.tx.usd != null && <span style={{ color: B.textMuted }}>≈ {usdThen(s.tx.usd)}</span>}
                  <span>{s.tx.time ? fmtDate(s.tx.time) : "unconfirmed"}</span>
                  {s.tx.recipients > 1 && <span style={{ color: B.textDim }}>biggest of {s.tx.recipients} recipients</span>}
                  <a href={txPath(r.chain, s.tx.hash)} style={{ color: B.accent, textDecoration: "none" }}><Mono ui={ui} style={{ fontSize: fs(11) }}>{short(s.tx.hash, 6)}</Mono></a>
                  {s.earliestSeen && <span style={{ flexBasis: "100%", fontSize: fs(11), color: B.textDim }}>This wallet has a long history: this is the earliest payment out we read, not necessarily its first.</span>}
                </div>
                <FlowWallet address={s.to} chain={r.chain} service={s.service} tags={s.tags} ui={ui} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: B.bg, border: `1px solid ${r.stop.reason === "service" || r.stop.reason === "sanctioned" ? B.primary + "88" : B.border}`, fontSize: fs(12), color: B.text, lineHeight: 1.55 }}>
            {(STOP_TEXT[r.stop.reason] || (() => r.stop.reason))(r.stop)}
            {r.steps.length > 1 && r.steps[0].tx.usd != null && <div style={{ marginTop: 4, fontSize: fs(11), color: B.textDim }}>The first hop moved about {usdThen(r.steps[0].tx.usd)} at the day's price; later hops carry part or all of it, mixed with other money.</div>}
          </div>
          <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {canContinue && <button onClick={more} disabled={state.loading} style={{ ...HP.navBtn, padding: "6px 12px", fontSize: fs(12), color: B.text, borderColor: B.accent }}>{state.loading ? "Following…" : `Keep going (${hops} more hops)`}</button>}
            {r.bundle && <button onClick={() => handOffBundle(r.bundle, `Where the money went from ${short(r.start, 8)}`)} style={{ ...HP.navBtn, padding: "6px 12px", fontSize: fs(12) }}>Show this path in the Graph</button>}
          </div>
        </div>
      )}
    </Box>
  );
}

function FlowWallet({ address, chain, service, tags = [], ui, first }) {
  const { B, fs } = ui;
  const all = [...(service ? [service.name] : []), ...tags.filter(t => !service || !t.includes(service.name))];
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "6px 10px", borderRadius: 8, background: service ? B.primary + "14" : first ? B.bg : "transparent", border: `1px solid ${service ? B.primary + "66" : B.border}` }}>
      <a href={walletPath(chain, address)} title={address} style={{ color: B.text, textDecoration: "none", fontWeight: 700 }}><Mono ui={ui} style={{ fontSize: fs(12) }}>{short(address, 10)}</Mono></a>
      {first && <span style={{ fontSize: fs(11), color: B.textDim }}>this wallet</span>}
      {all.slice(0, 4).map(t => <span key={t} style={{ fontSize: fs(10.5), fontWeight: 700, color: service && t === service.name ? B.primary : /^OFAC/.test(t) ? SANCTION_RED : /^Ransom/.test(t) ? RANSOM_ORANGE : B.textMuted, border: `1px solid ${B.border}`, borderRadius: 4, padding: "0 6px" }}>{t}</span>)}
    </div>
  );
}

export function KbWallet({ chain, address, ui, initialTab }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const { data, error, loading } = useJson(`/api/kb/wallet/${chain}/${encodeURIComponent(address)}`);
  // The same wallet as a Knowledge Base entity: relationships, reports, its STIX object.
  const { data: ent } = useJson(data ? `/api/kb/entity/${encodeURIComponent(data.key)}` : null);
  const chainState = useOnChain(data);
  const [tab, setTab] = useState(initialTab || "overview");
  useEffect(() => { setTab(initialTab || "overview"); }, [initialTab, chain, address]);
  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>;
  if (error) return <div style={{ ...card(ui), padding: 20, color: B.textMuted, fontSize: fs(13) }}>{error} <a href="#/kb/wallets" style={{ color: B.accent }}>Back to wallets</a></div>;
  const color = getConf(WALLET_TYPE).color;
  const sanctioned = data.sanctions.length > 0;
  const known = sanctioned || data.ransomware || data.reports.length || data.chats?.length || data.notes?.length;
  const rels = ent?.relationships || [];
  const subject = ent?.summary || { id: data.key, type: WALLET_TYPE, name: data.address, kind: "wallet", chain: data.chain, address: data.address };
  const c = chainState.c;
  const tabs = [
    { k: "overview", l: "Overview" },
    data.canLookUp ? { k: "transactions", l: "Transactions", n: c?.txs?.length || undefined } : null,
    TRACE_CHAINS.has(data.chain) ? { k: "trail", l: "Where did the money go?" } : null,
    { k: "relationships", l: "Relationships", n: rels.length },
    rels.length ? { k: "graph", l: "Graph" } : null,
    ent?.reports?.length ? { k: "reports", l: "Reported in", n: ent.reports.length } : null,
    { k: "stix", l: "STIX 2.1" },
  ].filter(Boolean);
  const tabBtn = (t) => (
    <button key={t.k} onClick={() => setTab(t.k)} aria-current={tab === t.k ? "page" : undefined} style={{ background: "none", border: "none", borderBottom: `2px solid ${tab === t.k ? B.primary : "transparent"}`, color: tab === t.k ? B.text : B.textDim, padding: "9px 14px", cursor: "pointer", fontSize: fs(13), fontWeight: tab === t.k ? 700 : 500, fontFamily: "'Satoshi', sans-serif", whiteSpace: "nowrap" }}>
      {t.l}{t.n ? <span style={{ color: B.textDim, fontWeight: 500, marginLeft: 6 }}>{nf(t.n)}</span> : null}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: fs(12), color: B.textDim }}><a href="#/kb/crypto-wallets" style={{ color: B.textDim, textDecoration: "none" }}>Indicators</a> / <a href="#/kb/wallets" style={{ color: B.textDim, textDecoration: "none" }}>Wallets</a> / {short(data.address, 8)}</div>

      <div style={{ ...card(ui), borderTop: `3px solid ${sanctioned ? SANCTION_RED : data.ransomware || data.chats?.length || data.notes?.length ? RANSOM_ORANGE : color}`, padding: "20px 22px" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><TypeIcon type={WALLET_TYPE} color={color} size={22} /></div>
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: fs(11), color, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2 }}>{data.chainName} wallet · indicator</span>
              {sanctioned && <Flag color={SANCTION_RED} ui={ui}>Sanctioned · OFAC</Flag>}
              {data.chats?.length > 0 && <Flag color={CHAT_PURPLE} ui={ui}>Given in a negotiation</Flag>}
              {data.notes?.length > 0 && <Flag color={CHAT_PURPLE} ui={ui}>In a ransom note</Flag>}
              {data.sanctions.some(s => s.ransomware?.length) && <Flag color={SANCTION_RED} ui={ui}>Ransomware · US Treasury</Flag>}
              {data.ransomware && <Flag color={RANSOM_ORANGE} ui={ui}>Ransom payments</Flag>}
              {data.reports.length > 0 && <Chip ui={ui}>Named in {data.reports.length} report{data.reports.length === 1 ? "" : "s"}</Chip>}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", margin: "6px 0 8px", flexWrap: "wrap" }}>
              <h1 style={{ fontSize: fs(20), fontWeight: 800, margin: 0, color: B.text, minWidth: 0 }}><Mono ui={ui} style={{ fontSize: fs(20) }}>{data.address}</Mono></h1>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {subject.confidence && <ConfidenceBadge confidence={subject.confidence} ui={ui} size="lg" />}
              {(subject.families || []).map(e => <a key={e.id} href={`#/kb/e/${encodeURIComponent(e.id)}`} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf("intrusion-set").color}>{e.name}</Chip></a>)}
              <CopyButton text={data.address} ui={ui} label="Copy address" />
              {data.explorer?.url && <a href={data.explorer.url} target="_blank" rel="noreferrer noopener" style={{ ...ui.HP.navBtn, padding: "4px 10px", fontSize: fs(11), textDecoration: "none" }}>{data.explorer.name} ↗</a>}
            </div>
          </div>
          <a href={graphHash(data.key)} title="See this wallet, what it's linked to and its transactions as an interactive graph" style={{ ...ui.HP.primaryBtn, padding: "8px 14px", textDecoration: "none", fontSize: fs(12) }}>View in Graph</a>
        </div>
        <StixBar apiKey={data.key} ui={ui} />
      </div>

      <div role="tablist" style={{ display: "flex", gap: 2, borderBottom: `1px solid ${B.border}`, overflowX: "auto" }}>{tabs.map(tabBtn)}</div>

      {tab === "overview" && <>
        {!known && (
          <div style={{ ...card(ui), padding: "12px 18px", fontSize: fs(12), color: B.textMuted, lineHeight: 1.6 }}>
            This address isn't in any of our sources (OFAC's sanctions list, negotiation chats, ransom notes, Ransomwhere, published reports). Most wallets you reach by following money are exchanges, payment services or ordinary people, so a link to a criminal wallet isn't evidence on its own.
          </div>
        )}
        <SourceBoxes data={data} ui={ui} />
        <OnChainSummary data={data} chainState={chainState} ui={ui} onTab={setTab} />
        {data.chain === "eth" && <OtherNetworks data={data} ui={ui} />}
      </>}
      {tab === "transactions" && <TxBox data={data} chainState={chainState} ui={ui} />}
      {tab === "trail" && <FlowBox data={data} ui={ui} />}
      {tab === "relationships" && (rels.length
        ? <div style={{ ...card(ui), overflow: "hidden" }}><RelationshipsTab relationships={rels} subject={short(data.address, 8)} ui={ui} /></div>
        : <div style={{ ...card(ui), padding: 18, fontSize: fs(13), color: B.textMuted }}>{ent ? "No relationships: none of our sources says anything about this address." : "Loading…"}</div>)}
      {tab === "graph" && <div style={{ ...card(ui), padding: 14 }}><RelationshipGraph subject={subject} relationships={rels} ui={ui} onShowBucket={() => setTab("relationships")} /></div>}
      {tab === "reports" && <ReportedInTab reports={ent?.reports || []} subject={subject} ui={ui} />}
      {tab === "stix" && (ent?.entity ? <StixExport id={data.key} entity={ent.entity} ui={ui} /> : <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>)}
    </div>
  );
}

// What each source says about the wallet (the Overview tab).
// The same Ethereum-format address on BNB Smart Chain, Polygon, Base and Arbitrum, on request (it
// counts as one blockchain lookup): balances, USDT and USDC, transactions sent, links to each history.
const OFAC_NETWORK = { BSC: "bsc", ARB: "arbitrum" };
function OtherNetworks({ data, ui }) {
  const { B, fs, HP } = ui;
  const listed = new Set(data.sanctions.flatMap(s => s.currencies || []).map(c => OFAC_NETWORK[c]).filter(Boolean));
  const [st, setSt] = useState({ loading: false, error: "", r: null });
  const look = () => {
    setSt({ loading: true, error: "", r: null });
    getJson(`/api/kb/wallet/eth/${encodeURIComponent(data.address)}/networks`).then(r => setSt({ loading: false, error: "", r })).catch(e => setSt({ loading: false, error: e.message, r: null }));
  };
  // OFAC listed it for one of these networks: look straight away.
  useEffect(() => { if (listed.size) look(); }, [data.address]); // eslint-disable-line react-hooks/exhaustive-deps
  const cell = { padding: "8px 10px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };
  return (
    <Box title="On other networks" ui={ui} right={st.r ? <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>from each network's public endpoint, {ago(st.r.fetchedAt)}</span> : null}>
      <p style={{ fontSize: fs(12), color: B.textMuted, margin: "0 0 10px", lineHeight: 1.55, maxWidth: "100ch" }}>
        The same address exists on every network that works like Ethereum, and sanctions lists and reports name some on BNB Smart Chain, Polygon, Base and Arbitrum.
        This shows what it holds there and how many transactions it has sent; the history is on each network's explorer. Following the money onward works on Ethereum only.
      </p>
      {!st.r && !st.loading && <button onClick={look} style={{ ...HP.navBtn, padding: "6px 12px", fontSize: fs(12) }}>Look it up on BNB Smart Chain, Polygon, Base and Arbitrum</button>}
      {st.loading && <div style={{ fontSize: fs(12), color: B.textDim }}>Asking the four networks…</div>}
      {st.error && <div style={{ fontSize: fs(12), color: SANCTION_RED }}>{st.error}</div>}
      {st.r && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead>
              <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                <th style={{ padding: "8px 10px", fontWeight: 700 }}>Network</th>
                <th style={{ padding: "8px 10px", fontWeight: 700 }}>Holds</th>
                <th style={{ padding: "8px 10px", fontWeight: 700, textAlign: "right" }}>Sent</th>
                <th style={{ padding: "8px 10px", fontWeight: 700 }}>History</th>
              </tr>
            </thead>
            <tbody>
              {st.r.networks.map(n => (
                <tr key={n.key}>
                  <td style={{ ...cell, fontWeight: 700, color: B.text, whiteSpace: "nowrap" }}>
                    {n.name}
                    {listed.has(n.key) && <div style={{ marginTop: 3 }}><Chip ui={ui} color={SANCTION_RED}>OFAC lists it here</Chip></div>}
                    {n.contract && <div style={{ fontSize: fs(11), color: B.textDim, fontWeight: 500 }}>a smart contract</div>}
                  </td>
                  <td style={{ ...cell, color: B.textMuted }}>
                    {n.error ? <span style={{ color: B.textDim }}>Didn't answer ({n.error})</span>
                      : <>{amount(n.balance, n.unit)}{n.tokens.map(t => <span key={t.symbol}> · <b style={{ color: B.text }}>{amount(t.amount, t.symbol)}</b></span>)}</>}
                  </td>
                  <td style={{ ...cell, textAlign: "right", color: n.sent ? B.text : B.textDim }}>{n.error ? "—" : nf(n.sent)}</td>
                  <td style={cell}><a href={n.explorer.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent, whiteSpace: "nowrap" }}>{n.explorer.name} ↗</a></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 8, lineHeight: 1.5 }}>“Sent” counts the transactions this address started (received ones aren't counted). Only USDT and USDC are shown: anyone can create a token, and thieves send worthless look-alikes to wallets.</div>
        </div>
      )}
    </Box>
  );
}

function SourceBoxes({ data, ui }) {
  const { B, fs } = ui;
  return <>
    {data.sanctions.map(s => (
      <Box key={s.ent} ui={ui} style={{ borderColor: SANCTION_RED + "55" }} title="Sanctioned by the US Treasury (OFAC)" right={<ConfidenceBadge confidence={s.confidence} ui={ui} />}>
        <div style={{ fontSize: fs(16), fontWeight: 800, marginBottom: 6 }}><a href={sanctionedPath(s.ent)} style={{ color: B.text, textDecoration: "none" }}>{s.name}</a></div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>{s.programs.map(p => <Chip key={p.code} ui={ui} title={p.code}>{p.label}</Chip>)}</div>
        <div style={{ display: "flex", gap: "6px 22px", flexWrap: "wrap" }}>
          <Fact k="Listed as" ui={ui}>{s.kind === "individual" ? "a person" : "an organisation"}</Fact>
          <Fact k="Address listed under" ui={ui}>{s.currencies.join(", ")}</Fact>
          <Fact k="Addresses on its listing" ui={ui}>{nf(s.wallets)}</Fact>
          {s.group && <Fact k="ATT&CK group" ui={ui}><a href={entityPath(s.group)} style={{ color: B.text }}>{s.group.name}</a></Fact>}
        </div>
        {s.linkedTo.length > 0 && <div style={{ fontSize: fs(12), color: B.textMuted, marginTop: 8 }}>Linked to: {s.linkedTo.join("; ")}</div>}
        {(s.ransomware || []).map(t => <TreasuryTie key={`${t.pr}:${t.role}`} t={t} ui={ui} />)}
        <div style={{ marginTop: 10, display: "flex", gap: 14, flexWrap: "wrap", fontSize: fs(12) }}>
          <a href={sanctionedPath(s.ent)} style={{ color: B.accent }}>All of its addresses →</a>
          <a href={s.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>OFAC's record ↗</a>
        </div>
      </Box>
    ))}

    {(data.notes || []).map(n => (
      <Box key={n.group} ui={ui} style={{ borderColor: CHAT_PURPLE + "66" }} title="Payment address in a ransom note" right={<ConfidenceBadge confidence={n.confidence} ui={ui} />}>
        <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.6, marginBottom: 8 }}>
          The ransom note of <b style={{ color: B.text }}>{n.entity?.name || n.group}</b> tells victims to pay to this address ({n.files.join(", ")}).
          {data.chatStats && Number(data.chatStats.received) > 0 ? <> It received <b style={{ color: B.text }}>{amount(data.chatStats.received, data.chatStats.unit)}</b> in all.</> : data.chatStats ? <> Nothing has been paid into it.</> : null}
          {" "}Some ransomware gave every victim a new address, so this may be one victim's.
        </div>
        {n.snippet && <blockquote style={{ margin: "0 0 10px", padding: "8px 12px", borderLeft: `3px solid ${CHAT_PURPLE}`, background: B.bg, borderRadius: 6, fontSize: fs(12), color: B.textMuted, wordBreak: "break-word" }}>“{n.snippet}”</blockquote>}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: fs(12) }}>
          {n.entity && <a href={groupHref(n.entity, "notes")} style={{ color: B.accent }}>{n.entity.name}'s ransom note →</a>}
          <a href={n.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>The notes in the ThreatLabz archive ↗</a>
        </div>
      </Box>
    ))}

    {(data.chats || []).map(c => (
      <Box key={`${c.group}${c.chatId}`} ui={ui} style={{ borderColor: CHAT_PURPLE + "66" }} title="Payment address given in a ransom negotiation" right={<ConfidenceBadge confidence={c.confidence} ui={ui} />}>
        <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.6, marginBottom: 8 }}>
          {c.party === "group" ? <>The <b style={{ color: B.text }}>{c.entity?.name || c.group}</b> negotiator gave this address to a victim as where to pay</> : <>A victim of <b style={{ color: B.text }}>{c.entity?.name || c.group}</b> quoted this address</>}
          {c.date ? <>, on {fmtDay(c.date)}</> : null}{c.mentions > 1 ? ` (${c.mentions} times in the chat)` : ""}.
          {data.chatStats && Number(data.chatStats.received) > 0 ? <> It went on to receive <b style={{ color: B.text }}>{amount(data.chatStats.received, data.chatStats.unit)}</b>{c.paidAfter ? ", and later messages in the chat talk about the payment" : ""}.</>
            : data.chatStats ? <> Nothing has been paid into it.</> : c.paidAfter ? <> Later messages in the chat talk about the payment.</> : null}
        </div>
        {c.snippet && <blockquote style={{ margin: "0 0 10px", padding: "8px 12px", borderLeft: `3px solid ${CHAT_PURPLE}`, background: B.bg, borderRadius: 6, fontSize: fs(12), color: B.textMuted, wordBreak: "break-word" }}>“{c.snippet}”</blockquote>}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: fs(12) }}>
          {c.entity && <a href={groupHref(c.entity, "negotiations")} style={{ color: B.accent }}>{c.entity.name}'s negotiations →</a>}
          <a href={c.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>The chat in the Ransomchats archive ↗</a>
        </div>
      </Box>
    ))}

    {data.ransomware && (
      <Box ui={ui} style={{ borderColor: RANSOM_ORANGE + "55" }} title="Ransomware payment address (Ransomwhere)" right={<ConfidenceBadge confidence={data.ransomware.confidence} ui={ui} />}>
        <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.6, marginBottom: 8 }}>
          Reported to Ransomwhere as a {data.ransomware.family ? <b style={{ color: B.text }}>{data.ransomware.family}</b> : "ransomware"} payment address{data.ransomware.entity ? <> (<a href={entityPath(data.ransomware.entity)} style={{ color: B.accent }}>{data.ransomware.entity.name} in the Knowledge Base</a>)</> : null}.
          It received <b style={{ color: B.text }}>{nf(data.ransomware.payments)}</b> payment{data.ransomware.payments === 1 ? "" : "s"}: {amount(data.ransomware.btc, "BTC")}, about <b style={{ color: B.text }}>{usd(data.ransomware.usd)}</b> at the time ({years(data.ransomware.first, data.ransomware.last)}).
        </div>
        {data.ransomPayments.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {data.ransomPayments.slice(0, 12).map(p => (
              <div key={p.hash} style={{ display: "flex", gap: 12, fontSize: fs(12), alignItems: "baseline", flexWrap: "wrap" }}>
                <span style={{ color: B.textMuted, width: 100 }}>{fmtDate(p.time)}</span>
                <span style={{ color: B.text, fontWeight: 700, width: 130, textAlign: "right" }}>{amount(p.btc, "BTC")}</span>
                <span style={{ color: B.textDim, width: 70, textAlign: "right" }}>{usd(p.usd)}</span>
                <a href={txPath("btc", p.hash)} style={{ color: B.accent, textDecoration: "none" }}><Mono ui={ui} style={{ fontSize: fs(11) }}>{short(p.hash, 6)}</Mono></a>
              </div>
            ))}
            {data.ransomPayments.length > 12 && <div style={{ fontSize: fs(11), color: B.textDim }}>and {data.ransomPayments.length - 12} more</div>}
          </div>
        )}
      </Box>
    )}

    {data.reports.length > 0 && (
      <Box ui={ui} title="Named in published reports">
        {data.reports.map(r => (
          <div key={r.id} style={{ display: "flex", gap: 10, padding: "6px 0", alignItems: "baseline", flexWrap: "wrap", fontSize: fs(12) }}>
            <a href={`#/kb/e/${encodeURIComponent(r.id)}`} style={{ flex: "1 1 260px", color: B.text, fontWeight: 600, textDecoration: "none" }}>{r.title}</a>
            <span style={{ color: B.textDim }}>{r.source} · {fmtDate(r.published)}{r.count > 1 ? ` · quoted ${r.count} times` : ""}</span>
            <ConfidenceBadge confidence={r.confidence} ui={ui} />
            {r.subject && (
              <div style={{ flexBasis: "100%", fontSize: fs(11), color: B.textDim, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                The report is mainly about <a href={groupHref(r.subject)} style={{ color: B.text }}>{r.subject.name}</a>, so the wallet is linked to it (inferred) <ConfidenceBadge confidence={r.linkConfidence} ui={ui} />
              </div>
            )}
          </div>
        ))}
      </Box>
    )}
  </>;
}

// ─── #/kb/tx/<chain>/<hash> ───

const TX_CHAINS = { btc: "Bitcoin", eth: "Ethereum", trx: "Tron", ltc: "Litecoin" };

export function KbTx({ chain, hash, ui }) {
  const { B, fs, HP, TypeIcon, getConf } = ui;
  const { data: t, error, loading } = useJson(`/api/kb/tx/${chain}/${encodeURIComponent(hash)}`);
  const color = getConf("cryptocurrency-transaction").color;
  const tryOthers = Object.keys(TX_CHAINS).filter(c => c !== chain && (c === "eth") === hash.startsWith("0x"));
  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Looking up the transaction…</div>;
  if (error) {
    return (
      <div style={{ ...card(ui), padding: 20, color: B.textMuted, fontSize: fs(13), lineHeight: 1.7 }}>
        {error}
        {tryOthers.length > 0 && <div>Is it on another blockchain? Try {tryOthers.map((c, i) => <span key={c}>{i ? " · " : ""}<a href={txPath(c, hash)} style={{ color: B.accent }}>{TX_CHAINS[c]}</a></span>)}</div>}
      </div>
    );
  }
  const side = (title, list, more) => (
    <Box title={title} ui={ui}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {list.map((p, i) => (
          <div key={`${p.address}${i}`} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", fontSize: fs(12) }}>
            <span style={{ flex: "1 1 200px", minWidth: 0 }}><Party p={p} chain={chain} ui={ui} /></span>
            <span style={{ whiteSpace: "nowrap", textAlign: "right" }}>
              <b style={{ color: B.text }}>{amount(p.amount, p.asset)}</b>
              {p.usd >= 0.01 && <span style={{ color: B.textDim, marginLeft: 6 }}>≈ {usdThen(p.usd)}</span>}
            </span>
          </div>
        ))}
        {!list.length && <span style={{ fontSize: fs(12), color: B.textDim }}>None</span>}
        {more > 0 && <span style={{ fontSize: fs(11), color: B.textDim }}>and {nf(more)} smaller</span>}
      </div>
    </Box>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: fs(12), color: B.textDim }}><a href="#/kb/wallets" style={{ color: B.textDim, textDecoration: "none" }}>Wallets</a> / transaction {short(t.hash, 6)}</div>
      <div style={{ ...card(ui), borderTop: `3px solid ${color}`, padding: "20px 22px" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><TypeIcon type="cryptocurrency-transaction" color={color} size={22} /></div>
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <div style={{ fontSize: fs(11), color, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2 }}>{TX_CHAINS[chain]} transaction</div>
            <h1 style={{ fontSize: fs(16), fontWeight: 800, margin: "6px 0 8px", color: B.text }}><Mono ui={ui} style={{ fontSize: fs(16) }}>{t.hash}</Mono></h1>
            <div style={{ display: "flex", gap: "6px 22px", flexWrap: "wrap" }}>
              <Fact k="When" ui={ui}>{t.time ? new Date(t.time).toLocaleString() : "unconfirmed"}</Fact>
              {t.block != null && <Fact k="Block" ui={ui}>{nf(t.block)}</Fact>}
              {t.outputs.some(p => p.usd != null) && <Fact k="Value on the day" ui={ui}>≈ {usdThen(t.outputs.reduce((s, p) => s + (p.usd || 0), 0))}{t.moreOut ? " (shown outputs)" : ""}</Fact>}
              {t.fee && <Fact k="Fee" ui={ui}>{amount(t.fee, { btc: "BTC", ltc: "LTC", eth: "ETH", trx: "TRX" }[chain])}</Fact>}
              {t.status === "failed" && <Fact k="Status" ui={ui}><span style={{ color: "#dc2626" }}>failed</span></Fact>}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              <CopyButton text={t.hash} ui={ui} label="Copy hash" />
              <a href={t.provider.url} target="_blank" rel="noreferrer noopener" style={{ ...HP.navBtn, padding: "4px 10px", fontSize: fs(11), textDecoration: "none" }}>{t.provider.site || t.provider.name} ↗</a>
            </div>
          </div>
        </div>
        <StixBar apiKey={`tx:${chain}:${t.hash}`} ui={ui} />
      </div>
      {t.note && <div style={{ ...card(ui), padding: "12px 18px", fontSize: fs(12), color: B.textMuted }}>{t.note}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))", gap: 14 }}>
        {side(chain === "eth" || chain === "trx" ? "From" : "Inputs (paid in)", t.inputs, t.moreIn)}
        {side(chain === "eth" || chain === "trx" ? "To" : "Outputs (paid out)", t.outputs, t.moreOut)}
      </div>
      {(chain === "btc" || chain === "ltc") && (
        <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.6 }}>
          In Bitcoin-style transactions every input is spent in full; what isn't paid to someone usually comes back to the sender as "change" at a new address, so one of the outputs is often the sender's own.
        </div>
      )}
    </div>
  );
}

// ─── #/kb/sanctioned/<id> ───

export function KbSanctioned({ ent, ui }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const { data: e, error, loading } = useJson(`/api/kb/sanctioned/${encodeURIComponent(ent)}`);
  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>;
  if (error) return <div style={{ ...card(ui), padding: 20, color: B.textMuted, fontSize: fs(13) }}>{error} <a href="#/kb/sanctioned" style={{ color: B.accent }}>Back to the list</a></div>;
  const color = getConf("identity").color;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: fs(12), color: B.textDim }}><a href="#/kb/sanctioned" style={{ color: B.textDim, textDecoration: "none" }}>Sanctioned Wallets</a> / {e.name}</div>
      <div style={{ ...card(ui), borderTop: `3px solid ${SANCTION_RED}`, padding: "20px 22px" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><TypeIcon type="identity" color={color} size={22} /></div>
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <div style={{ fontSize: fs(11), color: SANCTION_RED, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2 }}>Sanctioned by the US Treasury (OFAC) · {e.kind === "individual" ? "person" : "organisation"}</div>
            <h1 style={{ fontSize: fs(28), fontWeight: 900, margin: "2px 0 8px", color: B.text, wordBreak: "break-word" }}>{e.name}</h1>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{e.programs.map(p => <Chip key={p.code} ui={ui} title={p.code}>{p.label}</Chip>)}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <ConfidenceBadge confidence={e.confidence} ui={ui} />
            <a href={sanctionPath(`ofac-${e.ent}`)} style={{ ...ui.HP.navBtn, padding: "7px 13px", fontSize: fs(12), textDecoration: "none" }} title="Why it's sanctioned, and whether the UN, EU or UK sanction it too">Full listing</a>
            <a href={e.url} target="_blank" rel="noreferrer noopener" style={{ ...ui.HP.navBtn, padding: "7px 13px", fontSize: fs(12), textDecoration: "none" }}>OFAC's record ↗</a>
          </div>
        </div>
        <div style={{ display: "flex", gap: "6px 22px", flexWrap: "wrap", marginTop: 14, paddingTop: 12, borderTop: `1px solid ${B.border}` }}>
          {e.nationality && <Fact k="Nationality" ui={ui}>{e.nationality}</Fact>}
          {e.group && <Fact k="ATT&CK group" ui={ui}><a href={entityPath(e.group)} style={{ color: B.text }}>{e.group.name}</a> (same name)</Fact>}
          {e.websites.length > 0 && <Fact k="Websites listed" ui={ui}>{e.websites.join(", ")}</Fact>}
          {e.aliases.length > 0 && <Fact k="Also known as" ui={ui}>{e.aliases.slice(0, 10).join("; ")}{e.aliases.length > 10 ? "…" : ""}</Fact>}
          {e.linkedTo.length > 0 && <Fact k="Linked to" ui={ui}>{e.linkedTo.join("; ")}</Fact>}
        </div>
        <StixBar apiKey={`ofac:${e.ent}`} ui={ui} />
      </div>
      {e.ransomware?.length > 0 && (
        <Box title="Sanctioned for ransomware" ui={ui} style={{ borderColor: SANCTION_RED + "55" }} right={<a href="#/kb/sanctioned?tie=ransomware" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500, color: B.accent }}>All ransomware designations →</a>}>
          <div style={{ fontSize: fs(12.5), color: B.textMuted, lineHeight: 1.6 }}>
            OFAC's list doesn't say which ransomware group someone belongs to; Treasury's press release does.{e.ransomware.some(t => t.part && !t.partial) && e.wallets.length > 0 ? " Its addresses are linked to the group as indicators." : ""}
          </div>
          {e.ransomware.map(t => <TreasuryTie key={`${t.pr}:${t.role}`} t={t} ui={ui} />)}
        </Box>
      )}
      <Box title={e.wallets.length ? `${e.wallets.length} cryptocurrency address${e.wallets.length === 1 ? "" : "es"} on its listing` : "Cryptocurrency addresses"} ui={ui}>
        {e.wallets.length
          ? <WalletRows ui={ui} showPayments={e.wallets.some(w => w.ransomware)} items={e.wallets.map(w => ({ ...w, tags: [`Listed as ${w.currencies.join(", ")}`], family: w.ransomware ? w.ransomware.family || "" : undefined, usd: w.ransomware?.usd || 0 }))} />
          : <div style={{ fontSize: fs(12.5), color: B.textMuted }}>OFAC lists no cryptocurrency addresses for {e.kind === "individual" ? "this person" : "this organisation"}.</div>}
      </Box>
    </div>
  );
}

// ─── Profile tab: wallets tied to a group, malware family or report ───

export function WalletsTab({ wallets, subject, ui }) {
  const { B, fs } = ui;
  if (!wallets) return null;
  const isReport = wallets.fromReport;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.6 }}>
        {isReport
          ? <>Cryptocurrency addresses quoted in this report. Each one's checksum holds, so they're real addresses rather than look-alike strings.</>
          : <>
            {wallets.fromChats > 0 && <>{subject} gave victims <b style={{ color: B.text }}>{nf(wallets.fromChats)}</b> payment address{wallets.fromChats === 1 ? "" : "es"} in leaked negotiation chats{wallets.chatPaid ? <>; {nf(wallets.chatPaid)} of them received money, <b style={{ color: B.text }}>{amount(wallets.chatReceived, "BTC")}</b> in all</> : ""}. </>}
            {wallets.fromNotes > 0 && <>Its ransom notes print <b style={{ color: B.text }}>{nf(wallets.fromNotes)}</b> payment address{wallets.fromNotes === 1 ? "" : "es"}. </>}
            {wallets.fromTreasury > 0 && <><b style={{ color: B.text }}>{nf(wallets.fromTreasury)}</b> address{wallets.fromTreasury === 1 ? " belongs" : "es belong"} to people the US Treasury sanctioned as part of {subject} (below). </>}
            {wallets.fromReports > 0 && <>{nf(wallets.fromReports)} wallet{wallets.fromReports === 1 ? " is" : "s are"} quoted in reports mainly about {subject} (linked by inference). </>}
            {wallets.families?.length > 0 && <>Ransomwhere records {nf(wallets.total)} payment address{wallets.total === 1 ? "" : "es"} for {wallets.families.join(", ")}, which received about <b style={{ color: B.text }}>{usd(wallets.usd)}</b> in {nf(wallets.payments)} payments. </>}
            {wallets.sanctioned?.length > 0 && <>OFAC lists addresses for {wallets.sanctioned.map(s => s.name).join(", ")}, matched to {subject} by name. </>}
            {(wallets.families?.length === 1 || wallets.sanctioned?.length === 1) && <a href={wallets.families?.length === 1 ? `#/kb/wallets?family=${encodeURIComponent(wallets.families[0])}` : `#/kb/wallets?ent=${wallets.sanctioned[0].ent}`} style={{ color: B.accent }}>Open in Wallets →</a>}
          </>}
      </div>
      {wallets.items.length > 0 && <WalletRows items={wallets.items} ui={ui} showPayments={!isReport} />}
      {wallets.total > wallets.items.length && <div style={{ fontSize: fs(11), color: B.textDim }}>Showing {wallets.items.length} of {nf(wallets.total)}.</div>}
      {wallets.treasury?.length > 0 && (
        <Box title={`Sanctioned by the US Treasury (${wallets.treasury.length})`} ui={ui} style={{ borderColor: SANCTION_RED + "55" }} right={<a href="#/kb/sanctioned?tie=ransomware" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500, color: B.accent }}>All ransomware designations →</a>}>
          <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 8, lineHeight: 1.55 }}>People and businesses OFAC sanctioned as part of {subject} or for serving it, as Treasury's press releases say. Members' addresses are listed above; facilitators' aren't, since an exchange or host serves many customers.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {wallets.treasury.map(t => (
              <div key={t.ent} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", fontSize: fs(12) }}>
                <a href={sanctionedPath(t.ent)} style={{ color: B.text, fontWeight: 700, textDecoration: "none" }}>{t.name}</a>
                <Chip ui={ui} color={t.part ? SANCTION_RED : undefined}>{t.partial ? `${t.roleLabel}, partly attributed` : t.roleLabel}</Chip>
                <span style={{ color: B.textDim }}>{fmtDay(t.date)}{t.wallets ? ` · ${nf(t.wallets)} address${t.wallets === 1 ? "" : "es"}` : ""}</span>
                <a href={t.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent, marginLeft: "auto" }}>Press release ↗</a>
              </div>
            ))}
          </div>
        </Box>
      )}
    </div>
  );
}
